/* eslint-disable max-len */
import useCommon from '@utils/hooks/common';
import {useRouter} from 'next/router';
import {
  Button,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  Row,
  Skeleton,
  Tooltip,
  notification,
  theme,
} from 'antd';
import styles from './styles';
import CustomInput from '@components/CustomInput';
import {useEffect, useRef, useState} from 'react';
import useRequest from '@utils/hooks/useRequestCustom';
import authApi from '@utils/api/auth.api';
import {QrcodeOutlined} from '@ant-design/icons';
import CustomSelect from '@components/CustomSelect';
import classNames from 'classnames';
import QrScanner from 'qr-scanner';
import GradientButton from '@components/GradientButton';
import BigNumber from 'bignumber.js';
import OTPInput from 'react-otp-input';
import {DEFAULT_NETWORK_TOKEN_ID} from '../DepositTab';
import getTokenSymbol from '../getTokenSymbol';
import React from 'react';
import {InfoCircleOutlined} from '@ant-design/icons';
import {useTonAddress} from '@tonconnect/ui-react';
import {isTelegramMiniApp} from '@components/layout';
import {
  validateWithdrawalAddress,
  getChainTypeFromNetworkTokenId,
  parseWithdrawalError,
} from '@utils/withdrawalValidation';

// * for local testing, staging API gives insufficient data
const MOCK_NETWORKS = [
  {
    network: 'ERC20',
    token: 'USDT',
    fee: '2.6',
    decimalUnit: 18,
    contractAddress: '0x55d398326f99059ff775485246999027b3197955',
    networkTokenId: 'USDT_BSC',
  },
  {
    network: 'ERC20',
    token: 'USDT',
    fee: '2.6',
    decimalUnit: 6,
    contractAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
    networkTokenId: 'USDT_ETH',
  },
  {
    network: 'TRC20',
    token: 'USDT',
    fee: '5',
    decimalUnit: 6,
    contractAddress: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
    networkTokenId: 'USDT_TRX',
  },
];

type Network = {
  network: string;
  token: string;
  fee: string;
  contractAddress: string;
  decimalUnit: number;
  networkTokenId: string;
};

type Pages = 'withdrawal' | 'withdrawal_totp' | '2fa_required';

const WithdrawalTOTPPage = (props: {handleWithdrawal; onBack; isWithdrawing}) => {
  const {classes, t, user, system, theme} = useCommon({
    withTranslation: ['deposit_withdraw', 'auth'],
    withStyles: styles,
    observers: ['user', 'system'],
  });

  const {handleWithdrawal, onBack, isWithdrawing} = props;

  const [otp, setOtp] = useState('');

  const onOtpChange = async e => {
    setOtp(e);
    // if (e?.length === 6) {
    //   try {
    //     setSpinning(true);
    //     await onLogin(e);
    //     // TODO: Call login with otp API
    //   } catch (e) {
    //   } finally {
    //     setSpinning(false);
    //   }
    // }
  };

  return (
    <section className={classes.centeredContainer}>
      <Button
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transform: 'rotate(180deg)',
          padding: 0,
          height: 30,
        }}
        type="text"
        onClick={onBack}
      >
        <img src="/icon/auth/arrowForward.svg" alt="" />
      </Button>
      <div style={{...theme.tabTitleTextStyles, marginBottom: 15}}>
        {t('auth:two_factor_authentication')}
      </div>
      <div style={theme.secondaryTextStyles}>{t('auth:enter_code_in_authenticator_app')}</div>
      <OTPInput
        containerStyle={{margin: '30px 0px'}}
        value={otp}
        onChange={onOtpChange}
        numInputs={6}
        renderInput={props => <input {...props} />}
        inputType="number"
        inputStyle={{
          height: 50,
          width: 50,
          margin: '0 2px',
          backgroundColor: theme.inputBackground,
          border: 'none',
          borderRadius: '10px',
          padding: 0,
          color: theme.textColor,
        }}
      />

      <Button
        style={{
          height: 30,
          color: theme.linkColor,
        }}
        type="text"
        onClick={() => {
          system?.showModalSheet('setup2FA');
        }}
      >
        {t('lost_my_2fa_access')}
      </Button>

      <div className={classes.bottomSection}>
        <Divider className={classes.divider} />
        <GradientButton
          style={{height: 50}}
          onClick={() => {
            handleWithdrawal(otp);
          }}
          disabled={otp?.length < 6}
          loading={isWithdrawing}
        >
          {t('common:verify')}
        </GradientButton>
      </div>
    </section>
  );
};

const TwoFARequiredPage = () => {
  const {classes, t, user, system, theme} = useCommon({
    withTranslation: ['deposit_withdraw', 'auth'],
    withStyles: styles,
    observers: ['user', 'system'],
  });

  return (
    <section className={classes.centeredContainer}>
      <img src="/icon/auth/twoFA.svg" width={150} height={150} />
      <div style={{...theme.tabTitleTextStyles, marginBottom: 15}}>
        {t('auth:two_factor_authentication')}
      </div>
      <div style={theme.secondaryTextStyles}>{t('two_fa_required_msg')}</div>

      <div className={classes.bottomSection}>
        <Divider className={classes.divider} />
        <GradientButton
          style={{height: 50}}
          onClick={() => {
            system?.showModalSheet('setup2FA');
          }}
        >
          {t('setup_2fa')}
        </GradientButton>
      </div>
    </section>
  );
};

const WithdrawTab = ({isActive, inputRef}: {isActive?: boolean; inputRef?: React.RefObject<HTMLInputElement>}) => {
  const {classes, t, user, system} = useCommon({
    withTranslation: ['deposit_withdraw', 'auth'],
    withStyles: styles,
    observers: ['user', 'system'],
  });

  const AMOUNT_INITIAL_VALUE = '0';

  const router = useRouter();

  const [form] = Form.useForm();

  const scanner = useRef<QrScanner>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrBoxRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [qrOn, setQrOn] = useState<boolean>(false);
  const [withdrawAmount, setWithdrawAmount] = useState<string>(AMOUNT_INITIAL_VALUE);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [scannedResult, setScannedResult] = useState<string | undefined>('');
  const [regType, setRegType] = useState<'WALLET' | 'METAMASK' | 'EMAIL'>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<Network>(null);

  const [page, setPage] = useState<Pages>('withdrawal');
  const [withdrawalParams, setWithdrawalParams] = useState(null);

  // Use provided ref or create own
  const amountInputRef = inputRef || useRef<HTMLInputElement>(null);

  // Track if we've already focused once to prevent repeated focusing
  const hasFocusedOnce = useRef(false);

  const isWallet = regType === 'WALLET' || regType === 'METAMASK';

  const TELEGRAM_DEFAULT_NETWORK_TOKEN_ID = 'USDT_TON';
  const isTg = isTelegramMiniApp;
  // * for testing
  // const isTg = true;

  const {data: withdrawalInfoData, loading} = useRequest(authApi.getWithdrawalInfo, {
    // manual: true,
    onSuccess: result => {
      setRegType(result?.data?.registrationType);
      setNetworks(result?.data?.networks);
      // setNetworks(MOCK_NETWORKS);
      if (isTg) {
        form.setFieldValue('network', TELEGRAM_DEFAULT_NETWORK_TOKEN_ID);
        // form.setFieldValue(
        //   'blockchain',
        //   result?.data?.networks.find(item => item?.networkTokenId === ton)?.token
        // );
        setSelectedNetwork(
          result?.data?.networks.find(
            item => item?.networkTokenId === TELEGRAM_DEFAULT_NETWORK_TOKEN_ID
          )
        );
      } else {
        form.setFieldValue('network', DEFAULT_NETWORK_TOKEN_ID);
        // TODO @@ double check if it is needed
        // form.setFieldValue('blockchain', result?.data?.networks[0]?.token);
        form.setFieldValue(
          'blockchain',
          result?.data?.networks.find(item => item?.networkTokenId === DEFAULT_NETWORK_TOKEN_ID)
            ?.token
        );
        setSelectedNetwork(
          result?.data?.networks.find(item => item?.networkTokenId === DEFAULT_NETWORK_TOKEN_ID)
        );
      }
      // setSelectedNetwork(
      // MOCK_NETWORKS.find(item => item?.networkTokenId === DEFAULT_NETWORK_TOKEN_ID)
      // );
    },
  });
  // TODO @@ set reg type

  const {runAsync: withdraw, loading: isWithdrawing} = useRequest(authApi.withdraw, {
    manual: true,
  });

  const userFriendlyAddress = useTonAddress();

  useEffect(() => {
    if (
      user?.playerThirdPartyInfo?.registrationType === 'EMAIL' &&
      !user?.playerThirdPartyInfo?.isOTPLinked &&
      !isTg
    ) {
      setPage('2fa_required');
    }
  }, [user]);

  // * auto fill address for telegram
  useEffect(() => {
    if (selectedNetwork?.networkTokenId === TELEGRAM_DEFAULT_NETWORK_TOKEN_ID) {
      form.setFieldValue('address', userFriendlyAddress);
    } else if (selectedNetwork?.networkTokenId !== TELEGRAM_DEFAULT_NETWORK_TOKEN_ID && !isWallet) {
      // Only clear address if switching away from TON network and user previously had TON auto-filled
      const currentAddress = form.getFieldValue('address');
      if (currentAddress === userFriendlyAddress) {
        form.setFieldValue('address', '');
      }
      // Otherwise preserve user's manually entered address
    }
  }, [selectedNetwork]);

  // Simplified focus function for mobile
  const focusInput = React.useCallback((element: HTMLInputElement) => {
    if (!element) return;
    
    // Focus the element
    element.focus();
    
    // Set cursor at end
    const len = element.value.length;
    element.setSelectionRange(len, len);
    console.log('focusInput, hasFocusedOnce', hasFocusedOnce);
  }, []);

  // Callback ref that works with external inputRef from parent
  const inputCallbackRef = React.useCallback((node: HTMLInputElement) => {
    if (node) {
      // Store the node in our ref (either provided or internal)
      if (amountInputRef && 'current' in amountInputRef) {
        amountInputRef.current = node;
      }
    }
  }, []);

  // Effect for when tab becomes active - consolidated focus logic
  useEffect(() => {
    if (isActive && page === 'withdrawal' && !hasFocusedOnce.current) {
      const timeoutId = setTimeout(() => {
        if (amountInputRef?.current && !hasFocusedOnce.current) {
          focusInput(amountInputRef.current);
          hasFocusedOnce.current = true;
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isActive, page, focusInput, amountInputRef]);

  const FORM_INITIAL_VALUES = {blockchain: 'usdt', network: 'erc20'};

  const UNIT = 'USDT';

  const getReceivingAmount = () => {
    const receivingAmount = BigNumber(withdrawAmount)
      .minus(selectedNetwork?.fee || 0)
      .toFixed(2);
    return receivingAmount;
    // return Number.isNaN(Number(receivingAmount)) ? '--' : `${receivingAmount} ${UNIT}`;
  };

  const receivingAmount = getReceivingAmount();

  const isInvalidAmount =
    BigNumber(receivingAmount).isLessThanOrEqualTo(0) || Number.isNaN(Number(receivingAmount));

  const handleInputChange = e => {
    const {value} = e.target;

    if (value === '0' || value === '') {
      setWithdrawAmount(value);
    } else if (value === '.') {
      setWithdrawAmount('0.');
    } else {
      // * original: setWithdrawAmount(value.replace(/^0+/, ''));
      setWithdrawAmount(value.replace(/^0+(?=\d)/, ''));
    }
  };

  const onScanSuccess = (result: QrScanner.ScanResult) => {
    console.log(result);
    form.setFieldValue('address', result?.data);
    stopScanner();
  };

  const onScanFail = (err: string | Error) => {
    console.log(err);
  };

  const startScanner = () => {
    scanner?.current
      ?.start()
      .then(() => setQrOn(true))
      .catch(err => {
        if (err) setQrOn(false);
      });
  };

  const stopScanner = () => {
    setQrOn(false);
    scanner?.current?.stop();
  };

  useEffect(() => {
    if (videoRef?.current && !scanner.current) {
      // 👉 Instantiate the QR Scanner
      scanner.current = new QrScanner(videoRef?.current, onScanSuccess, {
        onDecodeError: onScanFail,
        // 📷 This is the camera facing mode. In mobile devices, "environment" means back camera and "user" means front camera.
        preferredCamera: 'environment',
        // 🖼 This will help us position our "QrFrame.svg" so that user can only scan when qr code is put in between our QrFrame.svg.
        highlightScanRegion: true,
        // 🔥 This will produce a yellow (default color) outline around the qr code that we scan, showing a proof that our qr-scanner is scanning that qr code.
        highlightCodeOutline: true,
        // 📦 A custom div which will pair with "highlightScanRegion" option above 👆. This gives us full control over our scan region.
        // overlay: qrBoxRef?.current || undefined,
      });
    }

    // 🧹 Clean up on unmount.
    // 🚨 This removes the QR Scanner from rendering and using camera when it is closed or removed from the UI.
    return () => {
      if (!videoRef?.current) {
        scanner?.current?.stop();
        scanner?.current?.destroy();
      }
    };
  }, [videoRef?.current, scanner?.current]);

  const handleWithdrawal = async (params: {values?; withdrawalInfoData; otp?}) => {
    const {values, withdrawalInfoData, otp} = params;

    const {amount, address, memo} = values;

    const walletAddress = withdrawalInfoData?.data?.registrationType === 'METAMASK' ? '' : address;

    const tokenId = networks?.find(
      item => item?.networkTokenId === values?.network
    )?.networkTokenId;

    try {
      const result = await withdraw({
        networkTokenId: tokenId,
        address: walletAddress,
        amount: Number(amount),
        accessToken: user?.playerToken,
        totpPassCode: otp ?? '',
        ...(memo ? {memo} : {}),

        // ...(otp ? {totpPassCode: otp} : {}),
      });
      console.log('withdraw - form - values', values);
      console.log('withdraw - result', result);
      if (result?.success) {
        user?.refreshPlayerInfo();
        // user?.getPlayerInfo();
        notification.success({
          message: t('create_withdraw_success_title'),
          description: t('create_withdraw_success_description'),
        });
        // TODO @@ update MobileHeader balance
        // user?.refreshPlayerInfo();
        // user?.getCoinWalletBalance();
        system?.dismissModalSheet();
      }
    } catch (error) {
      console.log('withdraw - error', error);

      // Parse and display user-friendly error message
      const errorMessage = parseWithdrawalError(error);
      notification.error({
        message: t('withdrawal_failed') || 'Withdrawal Failed',
        description: errorMessage,
        duration: 5,
      });
    }
  };

  if ((loading && page === 'withdrawal') || !regType)
    return <Skeleton paragraph={{rows: 5}} active />;

  return (
    <>
      {page === '2fa_required' && <TwoFARequiredPage />}
      {page === 'withdrawal_totp' && (
        <WithdrawalTOTPPage
          handleWithdrawal={otp => {
            handleWithdrawal({...withdrawalParams, otp});
          }}
          onBack={() => setPage('withdrawal')}
          isWithdrawing={isWithdrawing}
        />
      )}
      {page === 'withdrawal' && (
        <section className={classes.centeredContainer}>
          <input
            type="file"
            accept="image/png, image/jpeg"
            ref={fileInputRef}
            style={{display: 'none'}}
            onChange={async e => {
              console.log('event', e.target.files[0]);
              const file = e.target.files[0];
              try {
                const data = await QrScanner.scanImage(file, {returnDetailedScanResult: true});
                console.log('data', data);
                onScanSuccess(data);
              } catch (e) {
                notification.error({message: e});
              }
            }}
          />
          <Form
            className={classes.formContainer}
            layout="vertical"
            form={form}
            name="withdraw"
            scrollToFirstError
            requiredMark={false}
            // initialValues={FORM_INITIAL_VALUES}
            onFinish={async values => {
              const {address, amount, network} = values;
              const walletAddress =
                withdrawalInfoData?.data?.registrationType === 'METAMASK' ? '' : address;

              // Validate withdrawal address before proceeding
              // Use values.network (form state) instead of selectedNetwork (React state) to avoid race condition
              if (address && network) {
                const trimmedAddress = address.trim();
                const chainType = getChainTypeFromNetworkTokenId(network);

                if (!chainType) {
                  notification.error({
                    message: t('invalid_network') || 'Invalid Network',
                    description: 'Selected network is not supported for validation',
                    duration: 5,
                  });
                  return;
                }

                try {
                  const validationResult = validateWithdrawalAddress(trimmedAddress, chainType);
                  if (!validationResult.isValid) {
                    notification.error({
                      message: t('invalid_address') || 'Invalid Address',
                      description: validationResult.errorMessage,
                      duration: 5,
                    });
                    return;
                  }
                } catch (error) {
                  console.error('Address validation error:', error);
                  notification.error({
                    message: t('validation_error') || 'Validation Error',
                    description: 'An error occurred while validating the address. Please try again.',
                    duration: 5,
                  });
                  return;
                }
              }

              console.log(
                'onFinish - values',
                values,
                user?.playerThirdPartyInfo?.registrationType
              );
              if (
                user?.playerThirdPartyInfo?.registrationType === 'EMAIL' &&
                !isTg
                // TODO for testing
                // false
              ) {
                setPage('withdrawal_totp');
                setWithdrawalParams({values, withdrawalInfoData});
              } else {
                handleWithdrawal({values, withdrawalInfoData});
              }
            }}
          >
            <span className={classes.formItemLabel}>{t('withdrawal_amount')}</span>
            <div className={classes.availableWithdrawAmountRow}>
              <div style={{display: 'flex', alignItems: 'center'}}>
                <span style={{marginRight: 5}}>{t('available_cash_out_balance')}: </span>
                {withdrawalInfoData?.data?.maxWithdrawal
                  ? `${withdrawalInfoData?.data?.maxWithdrawal} ${UNIT}`
                  : ' -- '}
                <Tooltip placement="top" title={t('cash_out_info')}>
                  <InfoCircleOutlined style={{fontSize: 12, marginLeft: 5}} />
                </Tooltip>
              </div>
              <GradientButton
                style={{
                  borderRadius: 5,
                  margin: 0,
                  width: 53,
                  height: 20,
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => {
                  form.setFieldsValue({amount: withdrawalInfoData?.data?.maxWithdrawal});
                  setWithdrawAmount(withdrawalInfoData?.data?.maxWithdrawal);
                }}
              >
                {t('max')}
              </GradientButton>
            </div>
            {!user?.isTelegramBrowser && (
              <div>
                <span style={{marginRight: 5}}>{t('wallet_balance')}:</span>
                {withdrawalInfoData?.data?.walletBalance
                  ? `${withdrawalInfoData?.data?.walletBalance} ${UNIT}`
                  : ' -- '}
              </div>
            )}
            <Form.Item
              style={{textAlign: 'center'}}
              name="amount"
              initialValue={AMOUNT_INITIAL_VALUE}
              // label={<span className={classes.formItemLabel}>{t('available_amount')}</span>}
              rules={[
                {
                  required: true,
                  message: t('amount_required'),
                },
                {
                  validator: (rule, value, callback) => {
                    if (BigNumber(value).isGreaterThan(withdrawalInfoData?.data?.maxWithdrawal)) {
                      callback(t('amount_exceeds_available'));
                    } else if (
                      BigNumber(value).isLessThan(withdrawalInfoData?.data?.minWithdrawal)
                    ) {
                      callback(t('amount_below_minimum'));
                    } else if (BigNumber(value).isLessThanOrEqualTo(selectedNetwork?.fee)) {
                      callback(t('amount_below_fee'));
                    } else if (Number.isNaN(Number(value))) {
                      callback(t('invalid_number'));
                    } else {
                      callback();
                    }
                  },
                },
              ]}
            >
              {/* <CustomSelect
            // options={[{label: t('usdt'), value: 'usdt'}]}
            options={network.map(item => ({label: item.token, value: item.id}))}
            className={classes.customSelect}
          /> */}
              <div className={classes.inputSection}>
                <Input
                  ref={inputCallbackRef}
                  autoFocus
                  value={withdrawAmount}
                  className={classes.antDesignInput}
                  bordered={false}
                  onChange={handleInputChange}
                  type="tel"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  placeholder="0"
                  onFocus={(e) => {
                    // Ensure cursor is at the end when focused
                    const len = e.target.value.length;
                    e.target.setSelectionRange(len, len);
                  }}
                  // defaultValue={AMOUNT_INITIAL_VALUE}
                />

                <span className={classes.unitLabel}>{UNIT}</span>
              </div>
            </Form.Item>
            <Form.Item
              name="network"
              label={<span className={classes.formItemLabel}>{t('network')}</span>}
              rules={[
                {
                  required: true,
                  message: t('network_required'),
                },
              ]}
            >
              <CustomSelect
                // onChange={val => console.log('@@ val', val)}
                // options={[{label: 'ERC20', value: 'erc20'}]}
                // options={network.map(item => ({label: item?.network, value: item?.networkTokenId}))}
                options={networks?.map(item => ({
                  label: (
                    <span>{`${item?.network} - ${getTokenSymbol(item?.networkTokenId)}`}</span>
                  ),
                  value: item?.networkTokenId,
                }))}
                className={classes.customSelect}
                onChange={value => {
                  const selected = networks?.find(item => item?.networkTokenId === value);
                  setSelectedNetwork(selected);
                }}
              />
            </Form.Item>
            {/* <div className={classNames(classes.exchangeRateSectionBorder)}>
          <div className={classes.exchangeRateSection}>
            <span
              className={classes.metamaskExchangeRateText}
              data-text={`${t('exchange_rate')}(USDT/USD): ${data?.usdtRate || '--'}`}
            />
          </div>
        </div> */}
            {!isWallet && (
              <Form.Item
                name="address"
                label={<span className={classes.formItemLabel}>{t('address')}</span>}
                rules={[
                  {
                    required: true,
                    message: t('address_required'),
                  },
                ]}
                validateTrigger="onSubmit"
              >
                <CustomInput
                  className={classes.customInput}
                  placeholder={t('address_placeholder')}
                  suffix={
                    <QrcodeOutlined
                      onClick={() => {
                        startScanner();
                      }}
                    />
                  }
                />
              </Form.Item>
            )}
            
            {selectedNetwork?.networkTokenId === 'USDT_TON' && (
              <Form.Item
                name="memo"
                label={<span className={classes.formItemLabel}>{t('memo')}</span>}
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <CustomInput
                  className={classes.customInput}
                />
              </Form.Item>
            )}

            {/* <Form.Item
          name="amount"
          label={<span className={classes.formItemLabel}>{t('amount')}</span>}
          rules={[
            {
              required: true,
              message: t('auth:error_messages.amount_required'),
            },
          ]}
        >
          <CustomInput
            placeholder={t('amount_placeholder')}
            className={classes.customInput}
            // suffix="USDT"
            onChange={e => {
              setWithdrawAmount(e.target.value);
            }}
            suffix={
              <GradientButton
                // TODO @@ classes is not working
                style={{borderRadius: 5, width: 57, height: 40, marginRight: -6}}
                // className={classes.maxButton}
                onClick={() => {
                  form.setFieldsValue({amount: withdrawalInfoData?.data?.maxWithdrawal});
                  setWithdrawAmount(withdrawalInfoData?.data?.maxWithdrawal);
                }}
              >
                {t('max')}
              </GradientButton>
            }
          />
        </Form.Item> */}
            {/* <div style={{fontSize: '11px', opacity: '0.75', width: '100%'}}>
          {t('available_amount')}
        </div> */}

            <div className={classes.withdrawInfoSection}>
              <Row justify="space-between">
                <Col className={classes.withdrawalInfo}>{t('withdrawal_amount')}</Col>
                <Col className={classes.rightCol}>
                  {withdrawAmount ? `${withdrawAmount} ${UNIT}` : ' -- '}
                </Col>
              </Row>
              <Row justify="space-between">
                <Col className={classes.withdrawalInfo}>{t('withdrawal_fee')}</Col>
                <Col className={classes.rightCol}>
                  {selectedNetwork?.fee ? `${selectedNetwork?.fee} ${UNIT}` : '--'}
                </Col>
              </Row>
              {/* // TODO wait for BE  */}
              {/* <Row>
            <Col span={12}>{t('withdraw_cost')}</Col>
            <Col span={12} className={classes.rightCol}>
              -- USDT
            </Col>
          </Row> */}
              <Row justify="space-between">
                <Col className={classes.withdrawalInfo}>{t('receiving_amount')}</Col>
                {/* <Col className={classes.rightCol}>{getReceivingAmount()}</Col> */}
                <Col className={classes.rightCol}>
                  {isInvalidAmount ? '--' : `${receivingAmount} ${UNIT}`}
                </Col>
              </Row>
            </div>

            {/* withdraw button  */}
            <Form.Item noStyle>
              <div className={classes.buttonArea}>
                <GradientButton
                  disabled={
                    isInvalidAmount ||
                    BigNumber(form.getFieldValue('amount')).isGreaterThan(
                      withdrawalInfoData?.data?.maxWithdrawal
                    )
                  }
                  style={{height: 50, margin: 0, width: '99.5%'}}
                  loading={isWithdrawing}
                  className={classes.withdrawButton}
                  type="primary"
                  htmlType="submit"
                >
                  {t('withdraw')}
                </GradientButton>
              </div>
            </Form.Item>
          </Form>

          {/* Drawer  */}
          <Drawer
            open={qrOn}
            onClose={() => {
              stopScanner();
            }}
            height="100%"
            placement="bottom"
            forceRender
          >
            <video className={classes.video} ref={videoRef} />
            {/* <div ref={qrBoxRef}>
          <img width={256} height={256} />
        </div> */}

            <Button
              onClick={async () => {
                fileInputRef?.current?.click();
              }}
            >
              {t('pick_from_album')}
            </Button>
          </Drawer>
        </section>
      )}
    </>
  );
};

export default WithdrawTab;
